// src/pages/Live.js
import React, { useState, useEffect, useContext } from "react";
import Canvas from "../Components/Canvas";
import FooterSponsors from "../Components/FooterSponsors";

import { supabase } from "../supabase";
import "./Live.css";
import { getCurrentSession } from "../Utils/SessionUtils";

const LiveSupabase = ({ venueId, orderby, currentSession, transponderId = null}) => {
  const [lapTimes, setLapTimes] = useState([]);
  const [tags, setTags] = useState({});
  const [users, setUsers] = useState([]);

  // Fetch tags data
  useEffect(() => {
    const fetchTags = async () => {

      const { data, error } = await supabase.from("tags").select("*");
      if (error) {
        console.error("Error fetching tags:", error);
        return;
      }

      if (Array.isArray(data)) {
        // Filter out any undefined or null tags before reducing
        const tagsObject = data.reduce((acc, tag) => {
          if (tag && tag.tag) {
            acc[tag.tag] = tag;
          }
          return acc;
        }, {});
        setTags(tagsObject);
        console.log("Tags:", tagsObject);
      } else {
        console.warn("Unexpected data format for tags:", data);
      }
    };

    fetchTags();
  }, []);

  // Subscribe to real-time updates for tags
  useEffect(() => {
    const subscribeToTags = () => {
      const channel = supabase
        .channel("tags")
        .on("postgres_changes", { event: "*", schema: "public", table: "tags" }, (payload) => {
          // Re-fetch tags on change
          const fetchTags = async () => {
            const { data, error } = await supabase.from("tags").select("*");
            if (error) {
              console.error("Error fetching tags:", error);
              return;
            }

            if (Array.isArray(data)) {
              const tagsObject = data.reduce((acc, tag) => {
                if (tag && tag.tag) {
                  acc[tag.tag] = tag;
                }
                return acc;
              }, {});
              setTags(tagsObject);
              console.log("Updated Tags:", tagsObject);
            } else {
              console.warn("Unexpected data format for tags:", data);
            }
          };

          fetchTags();
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    const unsubscribe = subscribeToTags();

    return unsubscribe; // Cleanup subscription on unmount
  }, []);

  // Fetch user data from Supabase
  useEffect(() => {
    const fetchUsers = async () => {
      // Only fetch users if we have a current session
      if (!currentSession) return;

      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("session_id", currentSession.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching users:", error);
        return;
      }

      // Convert array to object with tag as key for efficient lookups
      const usersObject = data.reduce((acc, user) => {
        if (user.tag && !acc[user.tag]) {
          // Descending order, so first occurrence is most recently registered user with this tag
          acc[user.tag] = user;
        }
        return acc;
      }, {});

      setUsers(usersObject);

      //console.log('Users: ', usersObject);
    };

    fetchUsers(); // Initial fetch

    const refreshInterval = setInterval(fetchUsers, 2 * 60 * 1000); // Refresh every 2 minutes

    return () => clearInterval(refreshInterval);
  }, [currentSession]);


  // Fetch lap times data and subscribe to real-time updates
  useEffect(() => {
    // Don't proceed if currentSession is not available
    if (!currentSession) return;

    const fetchLapTimes = async () => {

      // Convert currentSessionStartTime from Europe/Brussels to UTC
      let start_time = new Date(currentSession.start_time).getTime();
      let end_time = new Date(currentSession.end_time).getTime();


      const { data, error } = venueId ?
        await supabase.from("laptimes")
          .select("*")
          .eq("venue_id", venueId)
        //.filter('"L07.rtcTime"', "gte",  start_time)
        //.filter('"L07.rtcTime"', "lte",  end_time)

        :
        await supabase
          .from("laptimes")
          .select("*")
      //.filter('"L07.rtcTime"', "gte",  start_time)
      //.filter('"L07.rtcTime"', "lte",  end_time)


      if (error) {
        console.error("Error fetching lap times:", error);
        return;
      }
      processLapTimes(data);
    };

    const subscribeToLapTimes = () => {
      const channel = supabase
        .channel("laptimes")
        .on("postgres_changes", { event: "*", schema: "public", table: "laptimes" }, (payload) => {
          // console.log("payload lap times:", payload);
          // Handle real-time updates
          if (payload.eventType === "UPDATE" && payload.new.location === "L07") {
            fetchLapTimes(); // Re-fetch data on change
          }
        })
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    fetchLapTimes();
    const unsubscribe = subscribeToLapTimes();

    return unsubscribe; // Cleanup subscription on unmount
  }, [tags, users, currentSession, venueId]);


  const processLapTimes = (data) => {
    const newLapTimes = [];
    const millis30minAgo = Date.now() - 30 * 60 * 1000;

    data.forEach((record) => {
      const tag = record.tag;
      //console.log(tags)
      if (tag !== "9992" && record["L07.rtcTime"] > millis30minAgo) {
        const name = users[tag]?.alias || users[tag]?.name || tags[tag]?.name || tag; // Replace tag with name if available
        newLapTimes.push({
          id: tag,
          tag: name,
          numLaps: record["L07.numLaps"],
          distance: ((parseInt(record["L07.numLaps"]) * 250) / 1000).toFixed(2),
          location: record.location,
          lapTime: record["L07.lapTime"].toFixed(3),
          elapsedTime: record["L07.rtcTime"] - record.firstPassing, // Store elapsed time in milliseconds for sorting
          elapsedTimeFormatted: new Date(record["L07.rtcTime"] - record.firstPassing)
            .toISOString()
            .substring(12, 19),
          updated: record["L07.rtcTime"]
        });
      }
    });

    // Sort lap times: Descending by numLaps, then ascending by elapsedTime
    if (orderby === "numLaps") {
      newLapTimes.sort((a, b) => {
        if (b.numLaps === a.numLaps) {
          return a.elapsedTime - b.elapsedTime; // Ascending by elapsedTime
        }
        return b.numLaps - a.numLaps; // Descending by numLaps
      });
    } else if (orderby === "latest") {
      console.log("Sorting by latest");
      newLapTimes.sort((a, b) => b.updated - a.updated); // Ascending by elapsedTime
    }

    setLapTimes(
      newLapTimes.map((item) => ({
        ...item,
        elapsedTime: item.elapsedTimeFormatted, // Replace elapsedTime with formatted string
      }))
    );
  };

  return (
    <Canvas showTime>
      <LapTimesTable lapTimes={lapTimes} transponderId={transponderId} />
      <FooterSponsors />
    </Canvas>
  );
};

const LapTimesTable = ({ lapTimes, transponderId =  null }) => {
  return (
    <table style={{ width: "100%" }}>
      <thead>
        <tr>
          <th>Naam/Tag</th>
          <th style={{ textAlign: "right" }}>Rondes</th>
          <th style={{ textAlign: "right" }}>Afstand (km)</th>
          <th style={{ textAlign: "right" }}>Rondetijd (s)</th>
          <th style={{ textAlign: "right" }}>Totale tijd</th>
        </tr>
      </thead>
      <tbody>
        {transponderId ? 
        lapTimes.filter((record) => transponderId ? record.id === transponderId : true)
          .map((record) => (
            <tr key={record.id} id={record.id}>
              <td style={{ width: "700px", height: "75px", maxWidth: "700px", maxHeight: "75px", overflow: "hidden" }}>
                {record.tag}
              </td>
              <td style={{ textAlign: "right", width: "200px" }}>{record.numLaps}</td>
              <td style={{ textAlign: "right", width: "280px" }}>{record.distance}</td>
              <td style={{ textAlign: "right", width: "280px" }}>{record.lapTime}</td>
              <td style={{ textAlign: "right", width: "300px" }}>{record.elapsedTime}</td>
            </tr>
          ))
        : lapTimes.map((record) => (
          <tr key={record.id} id={record.id}>
            <td style={{ width: "700px", height: "75px", maxWidth: "700px", maxHeight: "75px", overflow: "hidden" }}>
              {record.tag}
            </td>
            <td style={{ textAlign: "right", width: "200px" }}>{record.numLaps}</td>
            <td style={{ textAlign: "right", width: "280px" }}>{record.distance}</td>
            <td style={{ textAlign: "right", width: "280px" }}>{record.lapTime}</td>
            <td style={{ textAlign: "right", width: "300px" }}>{record.elapsedTime}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default LiveSupabase;